import { POIShape } from './POIShape';
import * as THREE from 'three';
import { AppSettingsData } from '../core/AppSettingsData';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { ThreeGeomFactory } from './ThreeGeomFactory';
import { POI } from '../poi/POI';
import { Logger } from '../core/Logger';

export class PlaneShape extends POIShape {
    private _resolution: number = 1.0;
    // Tracks aspect ratio of the last built geometry, so repeated change-events with the same
    // value skip the expensive recreateGeometry() pass.
    private _lastBuiltAspect: number | null = null;

    constructor(
        poi: POI,
        sceneSettings?: SceneSettingsData,
        appSettings?: AppSettingsData
    ) {
        super(poi, sceneSettings, appSettings);
        this._resolution = sceneSettings?.geometryResolution.value ?? 1.0;

        // Listen for changes to properties that affect geometry
        this.poi.poidata.cornerRadius?.on(() => this.recreateGeometry());
        this.sceneSettings?.geometryResolution.on(() => this.recreateGeometry());

        this.primaryMaterial.aspectRatio.on((newAspectRatio) => {
            const tolerance = 0.001;
            if (this._lastBuiltAspect !== null && Math.abs((newAspectRatio ?? 1) - this._lastBuiltAspect) < tolerance) {
                return; // No real change — skip recreate.
            }
            try { Logger.Instance.Info(`PlaneShape ${this.poi.name}: aspectRatio changed -> ${newAspectRatio} (recreate)`, 'Plane'); } catch {}
            this.recreateGeometry();
        });
        ////console.log(`🏗️ PlaneShape: Aspect ratio listener setup complete`);
    }

    protected override createGeometry(): THREE.BufferGeometry {
        const cornerRadius = this.poi.poidata.cornerRadius?.value ?? 0.0;
        const resolution = this._resolution;

        // Get effective aspect ratio (POI override or automatic from texture)
        const effectiveAspectRatio = this.getEffectiveAspectRatio();
        this._lastBuiltAspect = effectiveAspectRatio;
        try { Logger.Instance.Info(`PlaneShape ${this.poi.name}: createGeometry aspect=${effectiveAspectRatio}`, 'Plane'); } catch {}
        const width = 1 * effectiveAspectRatio;
        const height = 1;
        
        // The radius must be relative to the new geometry's dimensions.
        const relativeRadius = cornerRadius > 0 ? cornerRadius / Math.max(width, height) : 0;

        // Get texture aspect ratio for UV cropping
        const textureAspectRatio = this.getTextureAspectRatio();
        
        return ThreeGeomFactory.createRoundedPlane(width, height, relativeRadius, resolution, effectiveAspectRatio, textureAspectRatio);
    }

    /**
     * Get the effective aspect ratio to use for geometry creation
     */
    private getEffectiveAspectRatio(): number {
        // Check if POI has a manual aspect ratio override
        const poiAspectRatio = this.poi.poidata.aspectRatio?.value ?? 0;
        if (poiAspectRatio > 0) {
            return poiAspectRatio;
        }
        
        // Fallback to material aspect ratio (from texture or default)
        return this.primaryMaterial.aspectRatio.value ?? 1.0;
    }

    /**
     * Get the texture aspect ratio for UV cropping
     */
    private getTextureAspectRatio(): number | null {
        // Get the original texture aspect ratio (before POI override)
        const originalTextureRatio = (this.primaryMaterial as any).originalTextureAspectRatio ?? 1.0;
        return originalTextureRatio !== 1.0 ? originalTextureRatio : null;
    }
} 